import React, {useState, useEffect, useMemo, useCallback} from 'react'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody, Table, Loading} from '../../../components'
import {CustomCellDouble} from '../../../components/Table/columns/CustomCellDouble'
import {CustomCell} from '../../../components/Table/columns/CustomCell'
import {CustomHeader} from '../../../components/Table/columns/CustomHeader'
import {ListHeader} from '../../../components/Table/header/ListHeader'
import {ListLoading} from '../../../components/Table/ListLoading'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {purchaseOrderGetAll, purchaseOrderExcel} from '../../../redux/slices/purchase.slice'
import {projectLookup, allCompanyLookup, basicLookup} from '../../../redux/slices/lookup.slice'

import {useMemoizedStaticData} from '../../../utils/performanceOptimizations'

const selectPurchaseData = (state) => ({
  order: state.purchase.order,
  loading: state.purchase.loading,
})

const selectLookupData = (state) => ({
  projectLookupList: state.lookup.projectLookupList,
  companyLookup: state.lookup.companyLookup,
  PurchaseOrderStatus: state.lookup.PurchaseOrderStatus,
})

function List() {
  const intl = useIntl()
  const {order, loading} = useSelector(selectPurchaseData)
  const {projectLookupList, companyLookup, PurchaseOrderStatus} = useSelector(selectLookupData)
  const dispatch = useDispatch()
  const pageKey = 'ORDER'

  const [params, setParams] = useState()

  useEffect(() => {
    if (params) dispatch(purchaseOrderGetAll(params))
  }, [dispatch, params])

  useEffect(() => {
    dispatch(projectLookup())
    dispatch(allCompanyLookup())
    dispatch(basicLookup('PurchaseOrderStatus'))
  }, [dispatch])

  const handleSorting = useCallback((item) => {
    setParams((prevState) => ({...prevState, sort: item && item.sort, order: item && item.order}))
  }, [])

  const handleSearch = useCallback((item) => {
    setParams((prevState) => ({...prevState, name: item}))
  }, [])

  const handlePagination = useCallback((pagination) => {
    setParams((prevState) => ({...prevState, ...pagination}))
  }, [])

  const handleExcelExport = useCallback(() => {
    dispatch(
      purchaseOrderExcel({
        params,
        fileName: intl.formatMessage({id: 'BC.ORDER'}),
      })
    )
  }, [dispatch, params, intl])

  const handleFilterSubmit = useCallback((values) => {
    setParams({...values})
  }, [])

  const columns = useMemo(
    () => [
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'ORDER.SEARCH.NAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'name',
        Cell: ({...props}) => <CustomCell item={props} />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'ORDER.SEARCH.PROJECTNAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'projectName',
        Cell: ({...props}) => <CustomCell item={props} />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'ORDER.SEARCH.COMPANYNAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'companyName',
        Cell: ({...props}) => <CustomCell item={props} />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'ORDER.SEARCH.SHIPMENTDATE'})}
            className='min-w-125px'
          />
        ),
        accessor: 'shipmentDate',
        Cell: ({...props}) => <CustomCellDouble item={props} date='short' double='delayDay' />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'ORDER.SEARCH.PURCHASEREQUESTSTATUSNAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'purchaseOrderStatusName',
        Cell: ({...props}) => <CustomCell item={props} />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'LIST.LASTMODIFIEDINFO'})}
            className='min-w-125px'
          />
        ),
        accessor: 'lastModifiedBy',
        Cell: ({...props}) => <CustomCellDouble item={props} double='lastModifiedDate' />,
      },
    ],
    [intl, handleSorting]
  )

  const actionButtons = useMemoizedStaticData(
    () => [
      {
        title: 'BUTTON.EXCEL',
        onClick: handleExcelExport,
        icon: '/images/icons/duotune/arrows/arr044.svg',
        variant: 'light-primary',
      },
      {
        title: 'FILTER',
        icon: '/images/icons/duotune/general/gen031.svg',
        variant: 'light-primary',
        list: [
          {
            title: 'ORDER.SEARCH.PROJECTNAME',
            data: projectLookupList,
            name: 'projectId',
          },
          {
            title: 'ORDER.SEARCH.COMPANYNAME',
            data: companyLookup,
            name: 'companyId',
          },
          {
            title: 'ORDER.SEARCH.PURCHASEREQUESTSTATUSNAME',
            data: PurchaseOrderStatus,
            name: 'purchaseOrderStatusId',
          },
        ],
      },
    ],
    [handleExcelExport, projectLookupList, companyLookup, PurchaseOrderStatus]
  )

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'BC.ORDER'})}</PageTitle>
      <KTCard>
        <ListHeader
          search={handleSearch}
          actionButtons={actionButtons}
          onSubmit={handleFilterSubmit}
          pages={pageKey}
        />
        <KTCardBody>
          {!order && <ListLoading />}
          {order && (
            <Table
              items={order}
              cols={columns}
              handlePagination={handlePagination}
              paddless
              pages={pageKey}
            />
          )}
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}
export {List}
